import { errorMsg } from '../utils/errorMsg';
import React, { useState, useEffect, useMemo } from 'react';
import {
  Container, Typography, Box, Paper, Card, CardContent, Grid,
  Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, LinearProgress, TextField, Select, MenuItem,
  FormControl, InputLabel, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Alert, Tabs, Tab, Switch, FormControlLabel,
  Stack,
} from '@mui/material';
import {
  Dashboard as DashIcon, People as PeopleIcon,
  CheckCircle, Star, StarBorder, Block, Refresh,
  TrendingUp, AttachMoney, Edit, Delete,
  Add, Security, CloudUpload, Download, RestoreFromTrash,
  ChurchOutlined, PaidOutlined, LayersOutlined, VerifiedUserOutlined,
  BackupOutlined, BarChart as BarIcon,
} from '@mui/icons-material';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  registerCharts, barDataset, barOptions, doughnutOptions,
} from '../components/ChartTheme';
import ModuleHeader from '../components/ModuleHeader';
import api from '../axios';

registerCharts();

const TEAL = '#00F1A7';
const LIME = '#B4F300';
const MUTED = '#93A7A3';

const STATUS_CORES = {
  ativo: { bg: 'rgba(0,241,167,0.16)', color: TEAL },
  trial: { bg: 'rgba(59,130,246,0.18)', color: '#60A5FA' },
  pendente: { bg: 'rgba(255,194,75,0.16)', color: '#FFC24B' },
  suspenso: { bg: 'rgba(255,92,92,0.16)', color: '#FF5C5C' },
  reprovado: { bg: 'rgba(148,163,184,0.16)', color: '#94A3B8' },
};

const CORES_DONUT = {
  ativo: TEAL,
  trial: '#60A5FA',
  pendente: '#FFC24B',
  suspenso: '#FF5C5C',
  reprovado: '#94A3B8',
};

function fmtBRL(centavos) {
  return `R$ ${((centavos || 0) / 100).toFixed(2).replace('.', ',')}`;
}

export default function SuperAdminDashboard() {
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState('');
  const [tab, setTab] = useState(0);

  const [dashboard, setDashboard] = useState(null);
  const [igrejas, setIgrejas] = useState([]);
  const [planos, setPlanos] = useState([]);
  const [superAdmins, setSuperAdmins] = useState([]);
  const [backups, setBackups] = useState([]);
  const [statusFilter, setStatusFilter] = useState('todas');

  // Igrejas
  const [dialogIgreja, setDialogIgreja] = useState(false);
  const [dialogDelete, setDialogDelete] = useState(false);
  const [editIgreja, setEditIgreja] = useState(null);
  const [formIgreja, setFormIgreja] = useState({
    nome: '', cnpj: '', pastor_nome: '', email: '', telefone: '',
    endereco: '', bairro: '', cidade: '', estado: '', cep: '',
    status: 'pendente', plano_id: null, bonificada: false, observacoes: '',
  });

  // Super Admins
  const [dialogSuperAdmin, setDialogSuperAdmin] = useState(false);
  const [editSA, setEditSA] = useState(null);
  const [formSA, setFormSA] = useState({ nome: '', email: '', senha: '', ativo: true, isNew: true });

  // Planos
  const [dialogPlano, setDialogPlano] = useState(false);
  const [editPlano, setEditPlano] = useState(null);
  const [formPlano, setFormPlano] = useState({
    nome: '', slug: '', descricao: '', preco: '', max_membros: '',
    modulos: '', trial_dias: '7', ativo: true, ordem: '0',
  });

  const [backupLoading, setBackupLoading] = useState(false);
  const [importFile, setImportFile] = useState(null);

  useEffect(() => { carregar(); }, []);

  const carregar = async () => {
    setLoading(true);
    setErro('');
    try {
      const [dashRes, igrejasRes, planosRes, saRes, backupRes] = await Promise.all([
        api.get('/saas/dashboard'),
        api.get('/saas/igrejas'),
        api.get('/saas/planos'),
        api.get('/saas/super-admins'),
        api.get('/admin/backups'),
      ]);
      setDashboard(dashRes.data);
      setIgrejas(igrejasRes.data);
      setPlanos(planosRes.data);
      setSuperAdmins(saRes.data);
      setBackups(backupRes.data.backups || []);
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao carregar dados');
    }
    setLoading(false);
  };

  // ── Igrejas ────────────────────────────────────────────────
  const handleAprovar = async (id) => {
    try { await api.post(`/saas/igrejas/${id}/aprovar`); setSucesso('Igreja aprovada'); carregar(); } catch (err) { setErro('Erro ao aprovar'); }
  };
  const handleReprovar = async (id) => {
    try { await api.post(`/saas/igrejas/${id}/reprovar`); setSucesso('Igreja reprovada'); carregar(); } catch (err) { setErro('Erro ao reprovar'); }
  };
  const handleBonificar = async (id) => {
    try { await api.post(`/saas/igrejas/${id}/bonificar`); carregar(); } catch (err) { setErro('Erro ao bonificar'); }
  };
  const handleRemoverBonificacao = async (id) => {
    try { await api.post(`/saas/igrejas/${id}/remover-bonificacao`); carregar(); } catch (err) { setErro('Erro ao remover bonificação'); }
  };
  const handleSuspender = async (id) => {
    try { await api.post(`/saas/igrejas/${id}/suspender`); carregar(); } catch (err) { setErro('Erro ao suspender'); }
  };

  const openEditIgreja = (igreja) => {
    setEditIgreja(igreja);
    setFormIgreja({
      nome: igreja.nome || '', cnpj: igreja.cnpj || '', pastor_nome: igreja.pastor_nome || '',
      email: igreja.email || '', telefone: igreja.telefone || '', endereco: igreja.endereco || '',
      bairro: igreja.bairro || '', cidade: igreja.cidade || '', estado: igreja.estado || '',
      cep: igreja.cep || '', status: igreja.status || 'pendente', plano_id: igreja.plano_id || null,
      bonificada: igreja.bonificada || false, observacoes: igreja.observacoes || '',
    });
    setDialogIgreja(true);
  };

  const saveIgreja = async () => {
    try {
      if (editIgreja) await api.put(`/saas/igrejas/${editIgreja.id}`, formIgreja);
      setDialogIgreja(false);
      setSucesso('Igreja atualizada');
      carregar();
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao salvar');
    }
  };

  const deleteIgreja = async () => {
    try {
      await api.delete(`/saas/igrejas/${editIgreja.id}`);
      setDialogDelete(false); setDialogIgreja(false);
      setSucesso('Igreja excluída'); carregar();
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao excluir');
    }
  };

  // ── Super Admins ───────────────────────────────────────────
  const openNewSA = () => {
    setFormSA({ nome: '', email: '', senha: '', ativo: true, isNew: true });
    setDialogSuperAdmin(true);
  };
  const openEditSA = (sa) => {
    setEditSA(sa);
    setFormSA({ nome: sa.nome, email: sa.email, senha: '', ativo: sa.ativo, isNew: false });
    setDialogSuperAdmin(true);
  };
  const saveSA = async () => {
    try {
      if (formSA.isNew) await api.post('/saas/super-admins', formSA);
      else await api.put(`/saas/super-admins/${editSA.id}`, formSA);
      setDialogSuperAdmin(false); setSucesso('Super admin salvo'); carregar();
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao salvar');
    }
  };
  const deleteSA = async (id) => {
    try {
      await api.delete(`/saas/super-admins/${id}`);
      setSucesso('Super admin excluído'); carregar();
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao excluir');
    }
  };

  // ── Planos ─────────────────────────────────────────────────
  const openNewPlano = () => {
    setEditPlano(null);
    setFormPlano({ nome: '', slug: '', descricao: '', preco: '', max_membros: '', modulos: '', trial_dias: '7', ativo: true, ordem: '0' });
    setDialogPlano(true);
  };
  const openEditPlano = (p) => {
    setEditPlano(p);
    setFormPlano({
      nome: p.nome || '', slug: p.slug || '', descricao: p.descricao || '',
      preco: p.preco_mensal ? (p.preco_mensal / 100).toFixed(2) : '', max_membros: p.max_membros ?? '',
      modulos: Array.isArray(p.modulos) ? p.modulos.join(', ') : (p.modulos || ''),
      trial_dias: String(p.trial_dias ?? '7'), ativo: p.ativo ?? true, ordem: String(p.ordem ?? '0'),
    });
    setDialogPlano(true);
  };
  const savePlano = async () => {
    const payload = {
      nome: formPlano.nome,
      slug: formPlano.slug,
      descricao: formPlano.descricao,
      preco_mensal: Math.round(Number(formPlano.preco || 0) * 100),
      max_membros: formPlano.max_membros === '' ? null : Number(formPlano.max_membros),
      modulos: formPlano.modulos.split(',').map(s => s.trim()).filter(Boolean),
      trial_dias: Number(formPlano.trial_dias || 0),
      ativo: formPlano.ativo,
      ordem: Number(formPlano.ordem || 0),
    };
    try {
      if (editPlano) await api.put(`/saas/planos/${editPlano.id}`, payload);
      else await api.post('/saas/planos', payload);
      setDialogPlano(false); setSucesso('Plano salvo'); carregar();
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao salvar plano');
    }
  };
  const deletePlano = async (id) => {
    try {
      await api.delete(`/saas/planos/${id}`);
      setSucesso('Plano excluído'); carregar();
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao excluir plano');
    }
  };

  // ── Backups ────────────────────────────────────────────────
  const criarBackup = async () => {
    setBackupLoading(true); setErro('');
    try {
      const res = await api.post('/admin/backup');
      setSucesso(res.data.message || 'Backup criado'); carregar();
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao criar backup');
    }
    setBackupLoading(false);
  };
  const baixarBackup = async (filename) => {
    try {
      const res = await api.get(`/admin/backups/${encodeURIComponent(filename)}`, { responseType: 'blob' });
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setErro('Erro ao baixar backup');
    }
  };
  const restaurarBackup = async (filename) => {
    if (!window.confirm(`Restaurar o backup ${filename}? Esta ação sobrescreve os dados.`)) return;
    setBackupLoading(true); setErro('');
    try {
      const res = await api.post(`/admin/restore/${encodeURIComponent(filename)}`);
      setSucesso(res.data.message || 'Backup restaurado'); carregar();
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao restaurar backup');
    }
    setBackupLoading(false);
  };
  const deletarBackup = async (filename) => {
    if (!window.confirm(`Excluir o backup ${filename}?`)) return;
    try {
      await api.delete(`/admin/backups/${encodeURIComponent(filename)}`);
      setSucesso('Backup excluído'); carregar();
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao excluir backup');
    }
  };
  const importarBackup = async (dryRun) => {
    if (!importFile) { setErro('Selecione um arquivo .tar.gz'); return; }
    setBackupLoading(true); setErro('');
    const fd = new FormData();
    fd.append('file', importFile);
    fd.append('dry_run', String(dryRun));
    try {
      const res = await api.post('/admin/import-backup', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSucesso(res.data.message || 'Importação processada'); carregar();
    } catch (err) {
      setErro(errorMsg(err) || 'Erro ao importar backup');
    }
    setBackupLoading(false);
  };

  // ── Derivados (cálculo client-side) ────────────────────────
  const receitaPorPlano = useMemo(() => {
    const map = {};
    igrejas.forEach(ig => {
      if ((ig.status === 'ativo' || ig.status === 'trial') && !ig.bonificada && ig.plano) {
        map[ig.plano.nome] = (map[ig.plano.nome] || 0) + (ig.plano.preco_mensal || 0);
      }
    });
    return Object.entries(map)
      .map(([nome, valor]) => ({ nome, valor }))
      .sort((a, b) => b.valor - a.valor);
  }, [igrejas]);

  const igrejasPorStatus = useMemo(() => {
    const cont = { ativo: 0, trial: 0, pendente: 0, suspenso: 0, reprovado: 0 };
    igrejas.forEach(ig => { cont[ig.status] = (cont[ig.status] || 0) + 1; });
    return cont;
  }, [igrejas]);

  const receitaChartData = useMemo(() => ({
    labels: receitaPorPlano.length ? receitaPorPlano.map(p => p.nome) : ['Sem receita'],
    datasets: [{
      label: 'Receita (R$)',
      ...barDataset(receitaPorPlano.length ? receitaPorPlano.map(p => p.valor / 100) : [0]),
    }],
  }), [receitaPorPlano]);

  const statusChartData = useMemo(() => ({
    labels: ['Ativas', 'Trial', 'Pendentes', 'Suspensas', 'Reprovadas'],
    datasets: [{
      data: [igrejasPorStatus.ativo, igrejasPorStatus.trial, igrejasPorStatus.pendente, igrejasPorStatus.suspenso, igrejasPorStatus.reprovado],
      backgroundColor: [CORES_DONUT.ativo, CORES_DONUT.trial, CORES_DONUT.pendente, CORES_DONUT.suspenso, CORES_DONUT.reprovado],
      borderColor: '#0A141C',
      borderWidth: 2,
    }],
  }), [igrejasPorStatus]);

  const igrejasFiltradas = statusFilter === 'todas' ? igrejas : igrejas.filter(i => i.status === statusFilter);
  const handleField = (field) => (e) => setFormIgreja(f => ({ ...f, [field]: e.target.value }));

  const stats = [
    { label: 'Total Igrejas', value: dashboard?.total_igrejas ?? '—', icon: <ChurchOutlined />, color: TEAL, fs: '4.4rem' },
    { label: 'Ativas', value: dashboard?.igrejas_ativas ?? '—', icon: <CheckCircle />, color: TEAL, fs: '4.4rem' },
    { label: 'Trial', value: dashboard?.igrejas_trial ?? '—', icon: <TrendingUp />, color: '#60A5FA', fs: '4.4rem' },
    { label: 'Pendentes', value: dashboard?.igrejas_pendentes ?? '—', icon: <PeopleIcon />, color: '#FFC24B', fs: '4.4rem' },
    { label: 'Bonificadas', value: dashboard?.igrejas_bonificadas ?? '—', icon: <Star />, color: LIME, fs: '4.4rem' },
    { label: 'Suspensas', value: dashboard?.igrejas_suspensas ?? '—', icon: <Block />, color: '#FF5C5C', fs: '4.4rem' },
    { label: 'MRR Estimado', value: fmtBRL(dashboard?.faturamento_mensal_estimado), icon: <PaidOutlined />, color: TEAL, fs: '3.9rem' },
    { label: 'Receita ativa', value: fmtBRL(receitaPorPlano.reduce((a, p) => a + p.valor, 0)), icon: <AttachMoney />, color: LIME, fs: '3.9rem' },
  ];

  return (
    <>
      <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3 }, px: { xs: 1, md: 2 } }}>
        <ModuleHeader module="Sistema · Gestão do SaaS" title="Painel Episcopal" />

        {erro && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErro('')}>{erro}</Alert>}
        {sucesso && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSucesso('')}>{sucesso}</Alert>}

        {loading ? (
          <LinearProgress sx={{ mb: 4 }} />
        ) : (
          <>
            {/* KPIs */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {stats.map((s, i) => (
                <Grid item xs={6} sm={4} md={3} key={i}>
                  <Card sx={{ borderRadius: 1 }}>
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Box sx={{ color: s.color, display: 'flex' }}>{s.icon}</Box>
                        <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                      </Box>
                      <Typography sx={{ fontWeight: 800, fontSize: s.fs || '4.4rem', color: s.color, lineHeight: s.fs === '3.9rem' ? 1.15 : 1, whiteSpace: 'nowrap' }}>{s.value}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Tabs */}
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3, '& .MuiTab-root': { fontWeight: 600 } }}
              TabIndicatorProps={{ sx: { bgcolor: TEAL, height: 2, borderRadius: 2 } }}>
              <Tab label="Visão Geral" icon={<DashIcon />} iconPosition="start" />
              <Tab label="Igrejas" icon={<ChurchOutlined />} iconPosition="start" />
              <Tab label="Planos" icon={<LayersOutlined />} iconPosition="start" />
              <Tab label="Super Admins" icon={<Security />} iconPosition="start" />
              <Tab label="Backups" icon={<BackupOutlined />} iconPosition="start" />
            </Tabs>

            {/* ── Visão Geral ── */}
            {tab === 0 && (
              <>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={8}>
                    <Card sx={{ borderRadius: 1, p: 2, height: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <BarIcon sx={{ color: TEAL }} /><Typography sx={{ fontWeight: 700 }}>Receita mensal por plano</Typography>
                      </Box>
                      <Box sx={{ height: 280 }}>
                        <Bar data={receitaChartData} options={barOptions({ plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => `R$ ${c.parsed.y.toFixed(2).replace('.', ',')}` } } } })} />
                      </Box>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Card sx={{ borderRadius: 1, p: 2, height: '100%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                        <DashIcon sx={{ color: LIME }} /><Typography sx={{ fontWeight: 700 }}>Igrejas por status</Typography>
                      </Box>
                      <Box sx={{ height: 280 }}>
                        <Doughnut data={statusChartData} options={doughnutOptions()} />
                      </Box>
                    </Card>
                  </Grid>
                </Grid>

                <Card sx={{ borderRadius: 1 }}>
                  <Box sx={{ p: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <VerifiedUserOutlined sx={{ color: TEAL }} /><Typography sx={{ fontWeight: 700 }}>Últimas igrejas cadastradas</Typography>
                  </Box>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {['Igreja', 'Pastor', 'Cidade/UF', 'Status', 'Plano', 'Cadastro'].map(h => (
                            <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(dashboard?.ultimas_cadastradas || []).length === 0 ? (
                          <TableRow><TableCell colSpan={6} align="center">
                            <Typography color="text.secondary" sx={{ py: 3 }}>Nenhuma igreja cadastrada</Typography>
                          </TableCell></TableRow>
                        ) : (
                          (dashboard?.ultimas_cadastradas || []).map(ig => (
                            <TableRow key={ig.id} hover>
                              <TableCell><strong>{ig.nome}</strong></TableCell>
                              <TableCell>{ig.pastor_nome || '-'}</TableCell>
                              <TableCell>{ig.cidade ? `${ig.cidade}/${ig.estado}` : '-'}</TableCell>
                              <TableCell>
                                <Chip label={ig.status} size="small"
                                  sx={STATUS_CORES[ig.status] ? { ...STATUS_CORES[ig.status], fontWeight: 700 } : {}} />
                              </TableCell>
                              <TableCell>{ig.plano?.nome || '-'}</TableCell>
                              <TableCell>{ig.data_criacao ? new Date(ig.data_criacao).toLocaleDateString('pt-BR') : '-'}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              </>
            )}

            {/* ── Igrejas ── */}
            {tab === 1 && (
              <>
                <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
                  {[{ value: 'todas', label: 'Todas' }, { value: 'pendente', label: 'Pendentes' }, { value: 'trial', label: 'Trial' }, { value: 'ativo', label: 'Ativas' }, { value: 'suspenso', label: 'Suspensas' }].map(t => (
                    <Chip key={t.value} label={t.label} onClick={() => setStatusFilter(t.value)}
                      color={statusFilter === t.value ? 'primary' : 'default'}
                      variant={statusFilter === t.value ? 'filled' : 'outlined'} sx={{ fontWeight: 600 }} />
                  ))}
                </Box>

                <TableContainer component={Paper} sx={{ borderRadius: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {['Igreja', 'Pastor', 'Email', 'Status', 'Plano', 'Bonif.', 'Ações'].map(h => (
                          <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {igrejasFiltradas.length === 0 ? (
                        <TableRow><TableCell colSpan={7} align="center">
                          <Typography color="text.secondary" sx={{ py: 4 }}>Nenhuma igreja encontrada</Typography>
                        </TableCell></TableRow>
                      ) : (
                        igrejasFiltradas.map(igreja => (
                          <TableRow key={igreja.id} hover>
                            <TableCell><strong>{igreja.nome}</strong></TableCell>
                            <TableCell>{igreja.pastor_nome || '-'}</TableCell>
                            <TableCell>{igreja.email}</TableCell>
                            <TableCell>
                              <Chip label={igreja.status} size="small"
                                sx={STATUS_CORES[igreja.status] ? { ...STATUS_CORES[igreja.status], fontWeight: 700 } : {}} />
                            </TableCell>
                            <TableCell>{igreja.plano?.nome || '-'}</TableCell>
                            <TableCell>
                              {igreja.bonificada ? <Star sx={{ color: '#FFC24B', fontSize: 20 }} /> : <StarBorder sx={{ color: '#5E6E6A' }} />}
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.3, alignItems: 'center' }}>
                                {igreja.status === 'pendente' && (
                                  <>
                                    <Button size="small" variant="contained" color="success" onClick={() => handleAprovar(igreja.id)}>Aprovar</Button>
                                    <Button size="small" variant="outlined" color="warning" onClick={() => handleReprovar(igreja.id)}>Reprovar</Button>
                                  </>
                                )}
                                <Tooltip title="Editar"><IconButton size="small" onClick={() => openEditIgreja(igreja)}><Edit fontSize="small" /></IconButton></Tooltip>
                                <Tooltip title={igreja.bonificada ? 'Remover bonificação' : 'Bonificar'}>
                                  <IconButton size="small" onClick={() => igreja.bonificada ? handleRemoverBonificacao(igreja.id) : handleBonificar(igreja.id)}>
                                    {igreja.bonificada ? <Star sx={{ color: '#FFC24B' }} /> : <StarBorder />}
                                  </IconButton>
                                </Tooltip>
                                {igreja.status === 'ativo' && (
                                  <Tooltip title="Suspender"><IconButton size="small" color="error" onClick={() => handleSuspender(igreja.id)}><Block fontSize="small" /></IconButton></Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {/* ── Planos ── */}
            {tab === 2 && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                  <Button variant="contained" startIcon={<Add />} onClick={openNewPlano}>Novo Plano</Button>
                </Box>
                <TableContainer component={Paper} sx={{ borderRadius: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {['Plano', 'Slug', 'Preço/mês', 'Membros', 'Módulos', 'Trial', 'Ativo', 'Ações'].map(h => (
                          <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {planos.length === 0 ? (
                        <TableRow><TableCell colSpan={8} align="center"><Typography color="text.secondary" sx={{ py: 4 }}>Nenhum plano</Typography></TableCell></TableRow>
                      ) : (
                        planos.map(p => (
                          <TableRow key={p.id} hover>
                            <TableCell><strong>{p.nome}</strong></TableCell>
                            <TableCell>{p.slug}</TableCell>
                            <TableCell>{fmtBRL(p.preco_mensal)}</TableCell>
                            <TableCell>{p.max_membros ?? '∞'}</TableCell>
                            <TableCell>{(p.modulos || []).join(', ')}</TableCell>
                            <TableCell>{p.trial_dias}d</TableCell>
                            <TableCell><Chip label={p.ativo ? 'Sim' : 'Não'} size="small" color={p.ativo ? 'success' : 'default'} /></TableCell>
                            <TableCell>
                              <IconButton size="small" onClick={() => openEditPlano(p)}><Edit fontSize="small" /></IconButton>
                              <IconButton size="small" color="error" onClick={() => { if (window.confirm('Excluir este plano?')) deletePlano(p.id); }}><Delete fontSize="small" /></IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {/* ── Super Admins ── */}
            {tab === 3 && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                  <Button variant="contained" startIcon={<Add />} onClick={openNewSA}>Novo Super Admin</Button>
                </Box>
                <TableContainer component={Paper} sx={{ borderRadius: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {['Nome', 'Email', 'Ativo', 'Criado em', 'Ações'].map(h => (
                          <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {superAdmins.length === 0 ? (
                        <TableRow><TableCell colSpan={5} align="center"><Typography color="text.secondary" sx={{ py: 4 }}>Nenhum super admin</Typography></TableCell></TableRow>
                      ) : (
                        superAdmins.map(sa => (
                          <TableRow key={sa.id} hover>
                            <TableCell><strong>{sa.nome}</strong></TableCell>
                            <TableCell>{sa.email}</TableCell>
                            <TableCell><Chip label={sa.ativo ? 'Ativo' : 'Inativo'} size="small" color={sa.ativo ? 'success' : 'error'} /></TableCell>
                            <TableCell>{sa.data_criacao ? new Date(sa.data_criacao).toLocaleDateString('pt-BR') : '-'}</TableCell>
                            <TableCell>
                              <Tooltip title="Editar"><IconButton size="small" onClick={() => openEditSA(sa)}><Edit fontSize="small" /></IconButton></Tooltip>
                              <Tooltip title="Excluir"><IconButton size="small" color="error" onClick={() => { if (window.confirm('Excluir este super admin?')) deleteSA(sa.id); }}><Delete fontSize="small" /></IconButton></Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {/* ── Backups ── */}
            {tab === 4 && (
              <>
                <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                  <Button variant="contained" startIcon={<BackupOutlined />} onClick={criarBackup} disabled={backupLoading}>
                    Criar backup
                  </Button>
                  <Button variant="outlined" startIcon={<CloudUpload />} disabled={backupLoading || !importFile} onClick={() => importarBackup(true)}>
                    Testar import (dry-run)
                  </Button>
                  <Button variant="outlined" startIcon={<CloudUpload />} disabled={backupLoading || !importFile} onClick={() => importarBackup(false)}>
                    Importar
                  </Button>
                  <Button component="label" variant="text" startIcon={<CloudUpload />}>
                    Selecionar arquivo
                    <input type="file" accept=".tar.gz" hidden onChange={(e) => setImportFile(e.target.files[0] || null)} />
                  </Button>
                  {importFile && <Chip label={importFile.name} onDelete={() => setImportFile(null)} color="primary" size="small" />}
                </Box>

                <TableContainer component={Paper} sx={{ borderRadius: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {['Arquivo', 'Tipo', 'Tamanho', 'Criado em', 'Ações'].map(h => (
                          <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {backups.length === 0 ? (
                        <TableRow><TableCell colSpan={5} align="center"><Typography color="text.secondary" sx={{ py: 4 }}>Nenhum backup</Typography></TableCell></TableRow>
                      ) : (
                        backups.map(b => (
                          <TableRow key={b.filename} hover>
                            <TableCell sx={{ wordBreak: 'break-all' }}><strong>{b.filename}</strong></TableCell>
                            <TableCell><Chip label={b.tipo} size="small" color={b.tipo === 'manual' ? 'primary' : 'default'} /></TableCell>
                            <TableCell>{b.size_mb && b.size_mb > 0 ? `${b.size_mb} MB` : '—'}</TableCell>
                            <TableCell>{b.created_at ? new Date(b.created_at).toLocaleString('pt-BR') : '-'}</TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.3 }}>
                                <Tooltip title="Baixar"><IconButton size="small" color="primary" onClick={() => baixarBackup(b.filename)}><Download fontSize="small" /></IconButton></Tooltip>
                                <Tooltip title="Restaurar"><IconButton size="small" color="warning" onClick={() => restaurarBackup(b.filename)}><RestoreFromTrash fontSize="small" /></IconButton></Tooltip>
                                <Tooltip title="Excluir"><IconButton size="small" color="error" onClick={() => deletarBackup(b.filename)}><Delete fontSize="small" /></IconButton></Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Alert severity="info" sx={{ mt: 2 }}>Backup/restore restrito a super administradores. Restaurar substitui os dados atuais.</Alert>
              </>
            )}
          </>
        )}
      </Container>

      {/* Dialog: Editar Igreja */}
      <Dialog open={dialogIgreja} onClose={() => setDialogIgreja(false)} maxWidth="md" fullWidth>
        <DialogTitle>Editar Igreja</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}><TextField fullWidth label="Nome" value={formIgreja.nome} onChange={handleField('nome')} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="CNPJ" value={formIgreja.cnpj} onChange={handleField('cnpj')} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Pastor" value={formIgreja.pastor_nome} onChange={handleField('pastor_nome')} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Email" value={formIgreja.email} onChange={handleField('email')} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Telefone" value={formIgreja.telefone} onChange={handleField('telefone')} /></Grid>
            <Grid item xs={8}><TextField fullWidth label="Endereço" value={formIgreja.endereco} onChange={handleField('endereco')} /></Grid>
            <Grid item xs={4}><TextField fullWidth label="Bairro" value={formIgreja.bairro} onChange={handleField('bairro')} /></Grid>
            <Grid item xs={5}><TextField fullWidth label="Cidade" value={formIgreja.cidade} onChange={handleField('cidade')} /></Grid>
            <Grid item xs={2}><TextField fullWidth label="UF" value={formIgreja.estado} onChange={handleField('estado')} inputProps={{ maxLength: 2 }} /></Grid>
            <Grid item xs={3}><TextField fullWidth label="CEP" value={formIgreja.cep} onChange={handleField('cep')} /></Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Plano</InputLabel>
                <Select value={formIgreja.plano_id} label="Plano" onChange={handleField('plano_id')}>
                  <MenuItem value=""><em>Nenhum</em></MenuItem>
                  {planos.map(p => <MenuItem key={p.id} value={p.id}>{p.nome}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={formIgreja.status} label="Status" onChange={handleField('status')}>
                  {['pendente', 'trial', 'ativo', 'suspenso', 'reprovado'].map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel control={<Switch checked={formIgreja.bonificada} onChange={e => setFormIgreja(f => ({ ...f, bonificada: e.target.checked }))} />} label="Bonificada (100% gratuita)" />
            </Grid>
            <Grid item xs={12}><TextField fullWidth label="Observações" multiline rows={2} value={formIgreja.observacoes} onChange={handleField('observacoes')} /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button color="error" onClick={() => setDialogDelete(true)} startIcon={<Delete />}>Excluir</Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setDialogIgreja(false)}>Cancelar</Button>
          <Button variant="contained" onClick={saveIgreja}>Salvar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Confirmar exclusão igreja */}
      <Dialog open={dialogDelete} onClose={() => setDialogDelete(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent><Typography>Tem certeza que deseja excluir <strong>{editIgreja?.nome}</strong>?</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogDelete(false)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={deleteIgreja}>Excluir</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Super Admin */}
      <Dialog open={dialogSuperAdmin} onClose={() => setDialogSuperAdmin(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{formSA.isNew ? 'Novo Super Admin' : 'Editar Super Admin'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Nome" value={formSA.nome} onChange={e => setFormSA(f => ({ ...f, nome: e.target.value }))} sx={{ mt: 2, mb: 2 }} />
          <TextField fullWidth label="Email" value={formSA.email} onChange={e => setFormSA(f => ({ ...f, email: e.target.value }))} sx={{ mb: 2 }} />
          <TextField fullWidth label={formSA.isNew ? 'Senha' : 'Nova senha (deixe em branco para manter)'} type="password" value={formSA.senha} onChange={e => setFormSA(f => ({ ...f, senha: e.target.value }))} sx={{ mb: 2 }} />
          <FormControlLabel control={<Switch checked={formSA.ativo} onChange={e => setFormSA(f => ({ ...f, ativo: e.target.checked }))} />} label="Ativo" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogSuperAdmin(false)}>Cancelar</Button>
          <Button variant="contained" onClick={saveSA} disabled={formSA.isNew && !formSA.senha}>Salvar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Plano */}
      <Dialog open={dialogPlano} onClose={() => setDialogPlano(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editPlano ? 'Editar Plano' : 'Novo Plano'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}><TextField fullWidth label="Nome" value={formPlano.nome} onChange={e => setFormPlano(f => ({ ...f, nome: e.target.value }))} /></Grid>
            <Grid item xs={12} md={6}><TextField fullWidth label="Slug (ex: essencial)" value={formPlano.slug} onChange={e => setFormPlano(f => ({ ...f, slug: e.target.value }))} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Descrição" value={formPlano.descricao} onChange={e => setFormPlano(f => ({ ...f, descricao: e.target.value }))} /></Grid>
            <Grid item xs={12} md={4}><TextField fullWidth label="Preço mensal (R$)" value={formPlano.preco} onChange={e => setFormPlano(f => ({ ...f, preco: e.target.value }))} /></Grid>
            <Grid item xs={12} md={4}><TextField fullWidth label="Membros máx (vazio = ilimitado)" value={formPlano.max_membros} onChange={e => setFormPlano(f => ({ ...f, max_membros: e.target.value }))} /></Grid>
            <Grid item xs={12} md={4}><TextField fullWidth label="Dias de trial" value={formPlano.trial_dias} onChange={e => setFormPlano(f => ({ ...f, trial_dias: e.target.value }))} /></Grid>
            <Grid item xs={12} md={4}><TextField fullWidth label="Ordem" value={formPlano.ordem} onChange={e => setFormPlano(f => ({ ...f, ordem: e.target.value }))} /></Grid>
            <Grid item xs={12} md={8}><TextField fullWidth label="Módulos (separados por vírgula)" value={formPlano.modulos} onChange={e => setFormPlano(f => ({ ...f, modulos: e.target.value }))} /></Grid>
            <Grid item xs={12}><FormControlLabel control={<Switch checked={formPlano.ativo} onChange={e => setFormPlano(f => ({ ...f, ativo: e.target.checked }))} />} label="Plano ativo" /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogPlano(false)}>Cancelar</Button>
          <Button variant="contained" onClick={savePlano}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
